import {
  AppVersion,
  Colors,
  getMediaDevices,
  PopupButtonType,
  PopupIconType,
  PopupOnCloseData,
  showPopup,
  SettingsForm,
  SettingsFormRef,
  SettingsFormChangeData,
} from "@kalyzee/kast-web-sdk/web-components";
import clsx from "clsx";
import deepEqual from "deep-equal";
import React, { ForwardedRef, useCallback, useImperativeHandle, useMemo, useRef, useState } from "react";
import { SETTINGS_DEFAULT_KEY } from "../../helpers/storage";
import {
  createSettingsConfig,
  deleteSettingsConfig,
  encodeBase64Url,
  getSettingsConfigData,
  listSettingsConfigs,
  resolveActiveSettingsKey,
  saveSettingsConfigData,
  selectSettingsConfig,
  Settings,
  SettingsConfigInfo,
} from "../../helpers/settings";
import { toastError, toastSuccess } from "../../helpers/toast";
import { useRequestAdminPin } from "../../hooks/utils";
import {
  settingsSchema,
  SETTINGS_SCHEMA_VERSION,
  exportSettings,
  importSettings,
  importSettingsFromText,
  importSettingsFromUrl,
  ImportResult,
  prepareSettingsForForm,
  restoreSettingsFromForm,
  sanitizeSettings,
} from "../../helpers/settingsSchema";
import styles from "./SettingsPopup.module.css";
import workBoxStyles from "../workBoxes/WorkBox.module.css";
import Versions from "./Versions";
import { BoxOverlay, BoxContainer, Dropdown, OverlayContainer, Button, PrimaryButton, ReactIcons, Switch } from "@kalyzee/kalyzee-ui";
import { addPopup, removePopup } from "../../helpers/popup";
import { syntaxHighlightJson } from "../../helpers/json";
import ActionMenu from "./ActionMenu";
import JsonEditor from "./JsonEditor";

// ---- Small inline icons (feather-style) for the action menus ----

const iconProps: React.SVGProps<SVGSVGElement> = {
  width: 15,
  height: 15,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 2,
  strokeLinecap: "round",
  strokeLinejoin: "round",
};

const IconClipboard = () => (
  <svg {...iconProps}>
    <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
    <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
  </svg>
);

const IconDownload = () => (
  <svg {...iconProps}>
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

const IconFile = () => (
  <svg {...iconProps}>
    <path d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
    <polyline points="13 2 13 9 20 9" />
  </svg>
);

const IconLink = () => (
  <svg {...iconProps}>
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
);

// ---- JSON parse error formatting ----

const formatJsonParseError = (err: unknown, text: string): string => {
  const message = (err as Error)?.message ?? String(err);
  // Older V8 messages only carry "at position N" — derive line/column from it.
  const posMatch = message.match(/at position (\d+)/);
  if (posMatch && !/line \d+/.test(message)) {
    const pos = Math.min(parseInt(posMatch[1], 10), text.length);
    const before = text.slice(0, pos);
    const line = before.split("\n").length;
    const column = pos - before.lastIndexOf("\n");
    return `${message} (ligne ${line}, colonne ${column})`;
  }
  return message;
};

interface JsonViewStatus {
  errors: string[];
  warnings: string[];
  /** Form-shaped settings (sanitized) — only set when `errors` is empty. */
  sanitized?: any;
}

// ---- Prompt popup used by URL / paste imports and config creation ----

interface PromptPopupProps {
  title: string;
  description?: string;
  placeholder?: string;
  multiline?: boolean;
  submitLabel: string;
  onCancel: () => void;
  onSubmit: (value: string) => Promise<void>;
}

const PromptPopup = ({ title, description, placeholder, multiline, submitLabel, onCancel, onSubmit }: PromptPopupProps) => {
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!value.trim() || busy) return;
    setBusy(true);
    try {
      await onSubmit(value);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div onClick={(e) => e.stopPropagation()} className={styles.promptWrapper}>
      <BoxContainer
        topBarProps={{
          left: [],
          center: [<span key="title" className={workBoxStyles.label}>{title}</span>],
          right: [
            <PrimaryButton key="close" onClick={onCancel}>
              <ReactIcons.Close style={{ height: "0.8em" }} />
            </PrimaryButton>,
          ],
        }}
      >
        <div className={styles.promptContent}>
          {description ? <div className={styles.promptDescription}>{description}</div> : null}
          {multiline ? (
            <textarea
              className={styles.promptTextarea}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={placeholder}
              autoFocus
              spellCheck={false}
            />
          ) : (
            <input
              className={styles.promptInput}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={placeholder}
              autoFocus
              spellCheck={false}
              onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
            />
          )}
        </div>
        <div className={styles.promptFooter}>
          <Button.Simple label="Annuler" onClick={onCancel} />
          <Button.Simple label={busy ? "…" : submitLabel} onClick={submit} />
        </div>
      </BoxContainer>
    </div>
  );
};

export interface SettingsPopupProps {}

export interface SettingsPopupRef {
  openPopup: () => void;
}

const SettingsPopup = React.forwardRef(({}: SettingsPopupProps, forwardRef: ForwardedRef<SettingsPopupRef | undefined>) => {
  const requestAdminPin = useRequestAdminPin();
  const [isOpen, setIsOpen] = useState(false);

  const settingsFormRef = useRef<SettingsFormRef | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const formValidRef = useRef(true);
  const formErrorsRef = useRef<{ path: string; message: string }[]>([]);

  // JSON view (toggled by the header slider) — edits the real settings shape.
  const [jsonMode, setJsonMode] = useState(false);
  const [jsonText, setJsonText] = useState("");

  const jsonStatus = useMemo<JsonViewStatus | undefined>(() => {
    if (!jsonMode) return undefined;
    let parsed: any;
    try {
      parsed = JSON.parse(jsonText);
    } catch (err) {
      return { errors: [formatJsonParseError(err, jsonText)], warnings: [] };
    }
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return { errors: ["Le JSON doit être un objet de paramètres"], warnings: [] };
    }
    const { sanitized, warnings } = sanitizeSettings(parsed, settingsSchema);
    return { errors: [], warnings, sanitized };
  }, [jsonMode, jsonText]);

  // Multi-config: the popup views/edits one config at a time; a different one
  // may stay "active" (used by the app) until the check marks the viewed one.
  const [configs, setConfigs] = useState<SettingsConfigInfo[]>([]);
  const [viewedKey, setViewedKey] = useState<string>(() => resolveActiveSettingsKey());
  // Baseline of the viewed config as loaded — used to detect unsaved edits.
  const loadedDataRef = useRef<Settings>({});

  // Whether the active config changed (saved or re-selected) while the
  // settings were open — drives the "refresh the page?" proposal on leave.
  const activeTouchedRef = useRef(false);

  const viewedConfig = configs.find((c) => c.key === viewedKey);
  const viewedName = viewedConfig?.name ?? viewedKey;

  const tryOpenPopup = () => {
    requestAdminPin((success) => {
      if (!success) return;
      activeTouchedRef.current = false;
      const active = resolveActiveSettingsKey();
      const formShape = prepareSettingsForForm(getSettingsConfigData(active), settingsSchema);
      loadedDataRef.current = restoreSettingsFromForm(formShape, settingsSchema);
      setViewedKey(active);
      setConfigs(listSettingsConfigs());
      setJsonMode(false);
      setJsonText("");
      setIsOpen(true);
    });
  };

  const showErrorPopup = (title: string, content: React.ReactNode) => {
    showPopup({
      title,
      iconTitle: PopupIconType.ERROR,
      content,
      buttons: [{ type: PopupButtonType.OK, element: "OK" }],
    });
  };

  const showErrorsListPopup = (title: string, errors: React.ReactNode[]) => {
    showErrorPopup(
      title,
      <div className={styles.warningsContent}>
        <div style={{ marginBottom: 8 }}>Corrigez les erreurs suivantes :</div>
        <div className={styles.warningsList}>
          {errors.map((e, i) => (
            <div key={i} className={styles.warningItem}>• {e}</div>
          ))}
        </div>
      </div>,
    );
  };

  const showWarningsPopup = (title: string, warnings: string[], onContinue: () => void) => {
    showPopup({
      title,
      iconTitle: PopupIconType.WARNING,
      content: (
        <div className={styles.warningsContent}>
          <div className={styles.warningsList}>
            {warnings.map((w, i) => (
              <div key={i} className={styles.warningItem}>• {w}</div>
            ))}
          </div>
        </div>
      ),
      buttons: [
        { type: PopupButtonType.CANCEL, element: "Annuler" },
        {
          type: PopupButtonType.VALIDATE,
          element: <div style={{ color: Colors.getMainGreen() }}>Continuer quand même</div>,
        },
      ],
      onClose: (data: PopupOnCloseData) => {
        if (data.closeFromButton?.type === PopupButtonType.VALIDATE) {
          onContinue();
        }
      },
    });
  };

  /** Current settings in their real (persisted) shape, whatever the active view. */
  const getCurrentSettingsData = (): Settings => {
    if (jsonMode) {
      if (!jsonStatus || jsonStatus.errors.length) {
        throw new Error(jsonStatus?.errors.join(" — ") || "JSON invalide");
      }
      return restoreSettingsFromForm(jsonStatus.sanitized, settingsSchema);
    }
    const currentValue = settingsFormRef.current?.getValue() ?? {};
    return restoreSettingsFromForm(currentValue, settingsSchema);
  };

  // Write the viewed config to localStorage only — the refresh proposal
  // happens when leaving, and only if the active config was touched.
  const doSave = (): boolean => {
    try {
      const data = getCurrentSettingsData();
      saveSettingsConfigData(viewedKey, data);
      loadedDataRef.current = data;
      if (viewedKey === resolveActiveSettingsKey()) activeTouchedRef.current = true;
      toastSuccess(`Configuration « ${viewedName} » sauvegardée`);
      return true;
    } catch (err) {
      showErrorPopup(
        "Impossible de sauvegarder les paramètres",
        "Une erreur est apparue durant la sauvegarde des paramètres",
      );
      console.error("Error while saving parameters : ", err)
      return false;
    }
  };

  const showRefreshPopup = () => {
    let popup: React.ReactNode;
    popup = (
      <OverlayContainer
        visible
        style={{ width: "100dvw", height: "100dvh" }}
        overlay={
          <div onClick={(e) => e.stopPropagation()}>
            <BoxOverlay
              title="Voulez-vous rafraîchir la page pour appliquer vos modifications ?"
              level="success"
              buttons={[
                { label: "Non", onClick: () => removePopup(popup) },
                { label: "Oui, rafraîchir", style: { backgroundColor: "var(--color-green-1)" }, onClick: () => { removePopup(popup); window.location.reload(); } },
              ]}
            />
          </div>
        }
      />
    );
    addPopup(popup, "settings_apply_changes", "settings");
  };

  const closeSettings = () => {
    setIsOpen(false);
    if (activeTouchedRef.current) showRefreshPopup();
  };

  // Saving is forbidden while the active view holds errors — list them instead.
  const trySave = (): boolean => {
    if (jsonMode) {
      if (!jsonStatus || jsonStatus.errors.length) {
        showErrorsListPopup("Impossible de sauvegarder", jsonStatus?.errors ?? ["JSON invalide"]);
        return false;
      }
      return true;
    }
    if (!formValidRef.current) {
      showErrorsListPopup(
        "Impossible de sauvegarder",
        formErrorsRef.current.map((e) => (<><strong>{e.path}</strong>: {e.message}</>)),
      );
      return false;
    }
    return true;
  };

  // Saving keeps the settings open; `afterSave` carries the follow-up of the
  // flow that required saving first (closing, switching config).
  const save = (options?: { afterSave?: () => void }) => {
    if (!trySave()) return;
    const commit = () => {
      if (doSave()) options?.afterSave?.();
    };
    if (jsonMode && jsonStatus?.warnings.length) {
      showWarningsPopup("Avertissements avant sauvegarde", jsonStatus.warnings, commit);
    } else {
      commit();
    }
  };

  // ---- Multi-config management ----

  /** Load a config into the active view (form and, when shown, JSON editor). */
  const loadConfig = (key: string) => {
    const formShape = prepareSettingsForForm(getSettingsConfigData(key), settingsSchema);
    settingsFormRef.current?.setValue(JSON.stringify(formShape));
    const restored = restoreSettingsFromForm(formShape, settingsSchema);
    if (jsonMode) setJsonText(JSON.stringify(restored, null, 2));
    loadedDataRef.current = restored;
    setViewedKey(key);
  };

  const isViewDirty = (): boolean => {
    try {
      return !deepEqual(getCurrentSettingsData(), loadedDataRef.current);
    } catch (err) {
      return true; // unparseable JSON view — treat as unsaved edits
    }
  };

  const switchConfig = (key: string) => {
    if (key === viewedKey) return;
    if (!isViewDirty()) {
      loadConfig(key);
      return;
    }
    let popup: React.ReactNode;
    popup = (
      <OverlayContainer
        visible
        style={{ width: "100dvw", height: "100dvh" }}
        onClose={() => removePopup(popup)}
        overlay={
          <div onClick={(e) => e.stopPropagation()}>
            <BoxOverlay
              title={`Des modifications ne sont pas sauvegardées. Que faire avant de passer à « ${configs.find((c) => c.key === key)?.name ?? key} » ?`}
              level="question"
              buttons={[
                { label: "Annuler", onClick: () => removePopup(popup) },
                { label: "Ne pas sauvegarder", onClick: () => { removePopup(popup); loadConfig(key); } },
                {
                  label: "Sauvegarder",
                  style: { backgroundColor: "var(--color-green-1)" },
                  onClick: () => { removePopup(popup); save({ afterSave: () => loadConfig(key) }); },
                },
              ]}
            />
          </div>
        }
      />
    );
    addPopup(popup, "settings_switch_config", "settings");
  };

  /** The check in the config bar: make the viewed config the active one. */
  const handleSelectViewed = () => {
    if (viewedConfig?.selected) return; // cannot be unchecked — select another one instead
    selectSettingsConfig(viewedKey);
    setConfigs(listSettingsConfigs());
    activeTouchedRef.current = true;
    toastSuccess(`Configuration « ${viewedName} » activée`);
  };

  /** Delete the viewed config (confirmation first) and fall back to the default view. */
  const handleDeleteViewed = () => {
    const name = viewedName;
    let popup: React.ReactNode;
    popup = (
      <OverlayContainer
        visible
        style={{ width: "100dvw", height: "100dvh" }}
        onClose={() => removePopup(popup)}
        overlay={
          <div onClick={(e) => e.stopPropagation()}>
            <BoxOverlay
              title={`Supprimer la configuration « ${name} » ? Cette action est définitive.`}
              level="question"
              buttons={[
                { label: "Annuler", onClick: () => removePopup(popup) },
                {
                  label: "Supprimer",
                  style: { backgroundColor: "var(--color-red-1)" },
                  onClick: () => {
                    removePopup(popup);
                    try {
                      const wasActive = !!viewedConfig?.selected;
                      deleteSettingsConfig(viewedKey);
                      if (wasActive) activeTouchedRef.current = true; // the default took over
                      setConfigs(listSettingsConfigs());
                      loadConfig(SETTINGS_DEFAULT_KEY);
                      toastSuccess(`Configuration « ${name} » supprimée`);
                    } catch (err) {
                      showErrorPopup("Impossible de supprimer la configuration", (err as Error).message);
                    }
                  },
                },
              ]}
            />
          </div>
        }
      />
    );
    addPopup(popup, "settings_delete_config", "settings");
  };

  /** Empty the viewed config in the editor — takes effect once saved. */
  const handleClearConfig = () => {
    let popup: React.ReactNode;
    popup = (
      <OverlayContainer
        visible
        style={{ width: "100dvw", height: "100dvh" }}
        onClose={() => removePopup(popup)}
        overlay={
          <div onClick={(e) => e.stopPropagation()}>
            <BoxOverlay
              title={`Vider la configuration « ${viewedName} » ? Le contenu affiché sera remplacé par une configuration vide — sauvegardez pour appliquer.`}
              level="question"
              buttons={[
                { label: "Annuler", onClick: () => removePopup(popup) },
                {
                  label: "Vider",
                  style: { backgroundColor: "var(--color-red-1)" },
                  onClick: () => {
                    removePopup(popup);
                    settingsFormRef.current?.setValue("{}");
                    if (jsonMode) setJsonText("{}");
                  },
                },
              ]}
            />
          </div>
        }
      />
    );
    addPopup(popup, "settings_clear_config", "settings");
  };

  const openCreateConfigPrompt = () => {
    let popup: React.ReactNode;
    const close = () => removePopup(popup);
    popup = (
      <OverlayContainer
        visible
        style={{ width: "100dvw", height: "100dvh" }}
        onClose={close}
        overlay={
          <PromptPopup
            title="Nouvelle configuration"
            description="La configuration sera créée vide. Le nom sera normalisé (minuscules, espaces remplacés par des tirets)."
            placeholder="ex: Salle Averroès"
            submitLabel="Créer"
            onCancel={close}
            onSubmit={async (name) => {
              try {
                const info = createSettingsConfig(name, {});
                close();
                setConfigs(listSettingsConfigs());
                switchConfig(info.key); // guards unsaved edits before viewing the empty config
                toastSuccess(`Configuration « ${info.name} » créée`);
              } catch (err) {
                showErrorPopup("Impossible de créer la configuration", (err as Error).message);
              }
            }}
          />
        }
      />
    );
    addPopup(popup, "settings_create_config", "settings");
  };

  const handleFormChange = useCallback((data: SettingsFormChangeData) => {
    formValidRef.current = data.valid;
    formErrorsRef.current = data.errors;
  }, []);

  // ---- View toggle (form <-> JSON) ----

  const handleToggleJsonMode = (next: boolean) => {
    if (next) {
      try {
        const currentValue = settingsFormRef.current?.getValue() ?? {};
        const restored = restoreSettingsFromForm(currentValue, settingsSchema);
        setJsonText(JSON.stringify(restored, null, 2));
        setJsonMode(true);
      } catch (err) {
        showErrorPopup("Vue JSON", "Impossible de générer la vue JSON : " + (err as any)?.message);
      }
    } else {
      if (!jsonStatus || jsonStatus.errors.length) {
        showErrorsListPopup("Impossible de revenir au formulaire", jsonStatus?.errors ?? ["JSON invalide"]);
        return;
      }
      settingsFormRef.current?.setValue(JSON.stringify(jsonStatus.sanitized));
      setJsonMode(false);
    }
  };

  const handleReformatJson = () => {
    try {
      setJsonText(JSON.stringify(JSON.parse(jsonText), null, 2));
    } catch (err) { /* invalid JSON — nothing to reformat */ }
  };

  // ---- Export / copy ----

  const handleExport = () => {
    try {
      exportSettings(getCurrentSettingsData());
    } catch (err) {
      showErrorPopup("Erreur d'export", "Impossible d'exporter les paramètres : " + (err as any)?.message);
    }
  };

  // Copy the current content (including unsaved edits) in one of the
  // forms accepted by the `?settings=` URL query.
  const handleCopy = (encode?: (json: string) => string) => {
    try {
      const json = JSON.stringify(getCurrentSettingsData());
      const payload = encode ? encode(json) : json;
      navigator.clipboard.writeText(payload).then(() => {
        toastSuccess("Copié dans le presse-papier");
      }).catch((err) => {
        toastError("Impossible de copier dans le presse-papier");
        console.error("Failed to copy settings: ", err);
      });
    } catch (err) {
      showErrorPopup("Erreur de copie", "Impossible de copier les paramètres : " + (err as any)?.message);
    }
  };

  // ---- Import ----

  const applyImportResult = (result: ImportResult) => {
    const allWarnings: string[] = [];
    if (result.versionWarning) allWarnings.push(result.versionWarning);
    allWarnings.push(...result.warnings);

    const apply = () => {
      settingsFormRef.current?.setValue(JSON.stringify(result.settings));
      if (jsonMode) {
        const restored = restoreSettingsFromForm(result.settings, settingsSchema);
        setJsonText(JSON.stringify(restored, null, 2));
      }
      toastSuccess("Paramètres importés");
    };

    if (allWarnings.length > 0) {
      showWarningsPopup("Avertissements à l'import", allWarnings, apply);
    } else {
      apply();
    }
  };

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    try {
      applyImportResult(await importSettings(file));
    } catch (err) {
      showErrorPopup("Erreur d'import", (err as Error).message);
    }
  };

  const openImportPrompt = (options: {
    title: string;
    description?: string;
    placeholder?: string;
    multiline?: boolean;
    importFrom: (value: string) => Promise<ImportResult> | ImportResult;
  }) => {
    let popup: React.ReactNode;
    const close = () => removePopup(popup);
    popup = (
      <OverlayContainer
        visible
        style={{ width: "100dvw", height: "100dvh" }}
        onClose={close}
        overlay={
          <PromptPopup
            title={options.title}
            description={options.description}
            placeholder={options.placeholder}
            multiline={options.multiline}
            submitLabel="Importer"
            onCancel={close}
            onSubmit={async (value) => {
              try {
                const result = await options.importFrom(value);
                close();
                applyImportResult(result);
              } catch (err) {
                showErrorPopup("Erreur d'import", (err as Error).message);
              }
            }}
          />
        }
      />
    );
    addPopup(popup, "settings_import_prompt", "settings");
  };

  const handleClose = () => {
    if (!isViewDirty()) {
      closeSettings();
      return;
    }
    let popup: React.ReactNode;
    const dismiss = () => { removePopup(popup); closeSettings(); };
    const closePopupOnly = () => { removePopup(popup); };
    popup = (
      <OverlayContainer
        visible
        style={{ width: "100dvw", height: "100dvh" }}
        onClose={closePopupOnly}
        overlay={
          <div onClick={(e) => e.stopPropagation()}>
            <BoxOverlay
              title="Voulez-vous sauvegarder avant de fermer ?"
              level="question"
              buttons={[
                {
                  label: "Non, fermer",
                  onClick: dismiss,
                },
                {
                  label: "Sauvegarder",
                  style: { backgroundColor: "var(--color-green-1)" },
                  onClick: () => {
                    removePopup(popup);
                    save({ afterSave: closeSettings });
                  },
                },
              ]}
            />
          </div>
        }
      />
    );
    addPopup(popup, "settings_save_confirm", "settings");
  };

  useImperativeHandle(forwardRef, () => ({
    openPopup: tryOpenPopup,
  }));

  if (!isOpen) return null;

  const jsonHasErrors = !!jsonStatus?.errors.length;
  const jsonHasWarnings = !jsonHasErrors && !!jsonStatus?.warnings.length;

  return (
    <div className={styles.overlay}>
      {/* Config bar — choose / create / activate a config */}
      <div className={styles.configBar}>
        <span className={styles.configBarLabel}>Configuration :</span>
        <Dropdown
          options={configs.map((c) => c.key)}
          selected={viewedKey}
          onSelect={switchConfig}
          getLabel={(key) => {
            const config = configs.find((c) => c.key === key);
            return config ? `${config.name}${config.selected ? " (active)" : ""}` : key;
          }}
          width="240px"
          className={styles.configDropdown}
        />
        <Button.Simple label="+ Nouvelle configuration" className={styles.configBarBtn} onClick={openCreateConfigPrompt} />
        {viewedKey !== SETTINGS_DEFAULT_KEY ? (
          <Button.Simple
            label={
              <span className={styles.configDeleteLabel}>
                <ReactIcons.Trash style={{ height: "1em" }} />
                Supprimer
              </span>
            }
            className={clsx(styles.configBarBtn, styles.configDeleteBtn)}
            onClick={handleDeleteViewed}
          />
        ) : null}
        <div className={styles.configBarSpacer} />
        <label className={clsx(styles.configActiveCheck, viewedConfig?.selected && styles.configActiveCheckOn)}>
          <input
            type="checkbox"
            checked={!!viewedConfig?.selected}
            disabled={!!viewedConfig?.selected}
            onChange={handleSelectViewed}
          />
          Configuration active
        </label>
      </div>

      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerTitle}>
          Paramètres
          <span className={styles.headerConfigName}>{viewedName}</span>
          <span className={styles.headerVersion}>v{SETTINGS_SCHEMA_VERSION}</span>
        </div>
        <div className={styles.headerActions}>
          <div className={styles.viewToggle}>
            <span className={clsx(styles.viewToggleLabel, !jsonMode && styles.viewToggleLabelActive)}>Formulaire</span>
            <Switch value={jsonMode} onChange={handleToggleJsonMode} />
            <span className={clsx(styles.viewToggleLabel, jsonMode && styles.viewToggleLabelActive)}>JSON</span>
          </div>
          <Button.Simple
            label="Media Devices"
            className={styles.actionBtn}
            onClick={async () => {
              const devices = await getMediaDevices("both", "input");
              let popup: React.ReactNode;
              popup = (
                <OverlayContainer
                  visible
                  style={{ width: "100dvw", height: "100dvh" }}
                  onClose={() => removePopup(popup)}
                  overlay={
                    <div onClick={(e) => e.stopPropagation()} className={styles.devicesWorkBoxWrapper}>
                      <BoxContainer
                        topBarProps={{
                          left: [],
                          center: [<span key="title" className={workBoxStyles.label}>Media Devices</span>],
                          right: [
                            <PrimaryButton key="close" onClick={() => removePopup(popup)}>
                              <ReactIcons.Close style={{ height: "0.8em" }} />
                            </PrimaryButton>,
                          ],
                        }}
                      >
                        <div className={styles.devicesWorkBoxContent}>
                          <pre className={styles.devicesWorkBoxPre} dangerouslySetInnerHTML={{ __html: syntaxHighlightJson(devices) }} />
                        </div>
                        <div className={styles.devicesWorkBoxFooter}>
                          <Button.Simple label="Fermer" onClick={() => { removePopup(popup); }} />
                        </div>
                      </BoxContainer>
                    </div>
                  }
                />
              );
              addPopup(popup, "settings_media_devices", "settings");
            }}
          />
          <ActionMenu
            label="Exporter"
            buttonClassName={styles.actionBtn}
            items={[
              { icon: <IconDownload />, label: "Télécharger (.json)", onClick: handleExport },
              { icon: <IconClipboard />, label: "Copier JSON", onClick: () => handleCopy() },
              { icon: <IconClipboard />, label: "Copier JSON échappé (URL)", onClick: () => handleCopy(encodeURIComponent) },
              { icon: <IconClipboard />, label: "Copier base64url", onClick: () => handleCopy(encodeBase64Url) },
            ]}
          />
          <ActionMenu
            label="Importer"
            buttonClassName={styles.actionBtn}
            items={[
              { icon: <IconFile />, label: "Depuis un fichier…", onClick: () => fileInputRef.current?.click() },
              {
                icon: <IconLink />,
                label: "Depuis une URL…",
                onClick: () => openImportPrompt({
                  title: "Importer depuis une URL",
                  description: "URL d'un fichier JSON de paramètres, ou lien de l'application contenant « ?settings=… ».",
                  placeholder: "https://…",
                  importFrom: importSettingsFromUrl,
                }),
              },
              {
                icon: <IconClipboard />,
                label: "Coller JSON / base64…",
                onClick: () => openImportPrompt({
                  title: "Importer JSON / base64",
                  description: "Collez un JSON de paramètres, ou sa version encodée en base64 / base64url.",
                  placeholder: '{ "roomId": "…" }  ou  eyJyb29tSWQi…',
                  multiline: true,
                  importFrom: importSettingsFromText,
                }),
              },
            ]}
          />
          <input
            ref={fileInputRef}
            type="file"
            accept=".json,application/json"
            style={{ display: 'none' }}
            onChange={handleFileSelected as any}
          />
        </div>
      </div>

      {/* Body — scrollable form. Seeded from the stored layer: showing the
          resolved view would bake transient env/URL values into localStorage
          on save. The form stays mounted (hidden) in JSON mode so its state
          and ref survive the round-trip. */}
      <div className={clsx(styles.body, jsonMode && styles.bodyJson)}>
        <div className={styles.settingsFormContainer} style={jsonMode ? { display: "none" } : undefined}>
          <SettingsForm
            ref={settingsFormRef}
            schema={settingsSchema}
            defaultValue={JSON.stringify(prepareSettingsForForm(getSettingsConfigData(viewedKey), settingsSchema))}
            onChange={handleFormChange}
            style={{ width: '100%' }}
          />
        </div>
        {jsonMode ? (
          <div className={styles.jsonView}>
            <JsonEditor value={jsonText} onChange={setJsonText} className={styles.jsonViewEditor} />
            <div
              className={clsx(
                styles.jsonStatusBar,
                jsonHasErrors && styles.jsonStatusBarError,
                jsonHasWarnings && styles.jsonStatusBarWarning,
              )}
            >
              <div className={styles.jsonStatusHeader}>
                <span className={styles.jsonStatusTitle}>
                  {jsonHasErrors
                    ? "✕ JSON invalide — sauvegarde impossible"
                    : jsonHasWarnings
                      ? `⚠ JSON valide — ${jsonStatus!.warnings.length} avertissement${jsonStatus!.warnings.length > 1 ? "s" : ""}`
                      : "✓ JSON valide"}
                </span>
                {!jsonHasErrors ? (
                  <button type="button" className={styles.jsonStatusAction} onClick={handleReformatJson}>
                    Reformater
                  </button>
                ) : null}
              </div>
              {jsonHasErrors || jsonHasWarnings ? (
                <div className={styles.jsonStatusList}>
                  {(jsonHasErrors ? jsonStatus!.errors : jsonStatus!.warnings).map((msg, i) => (
                    <div key={i} className={styles.jsonStatusItem}>• {msg}</div>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        ) : null}
      </div>

      {/* Footer */}
      <div className={styles.footer}>
        <div className={styles.footerVersions}>
          <Versions />
          <AppVersion />
        </div>
        <div className={styles.footerButtons}>
          <Button.Simple
            label={
              <span className={styles.configDeleteLabel}>
                <ReactIcons.Trash style={{ height: "0.9em" }} />
                Vider la configuration
              </span>
            }
            className={clsx(styles.actionBtn, styles.configDeleteBtn)}
            onClick={handleClearConfig}
          />
          <Button.Simple label="Fermer" className={styles.actionBtn} onClick={handleClose} />
          <Button.Simple label="Sauvegarder" className={styles.actionBtn} onClick={() => save()} />
        </div>
      </div>
    </div>
  );
});

export default SettingsPopup;
